<template>
    <div>
        <v-container fluid>
            <v-card elevation="5">
                <v-toolbar flat dense class="elevation-5 mb-2" height="40">
                    <v-toolbar-title class="title">
                        {{ $t("module.assigned_pay_section_tools") }}
                    </v-toolbar-title>
                    <ImportModel file-name="PlanPaie.xlsx"/>
                    <v-spacer/>

                    <v-btn
                        depressed
                        class="ma-2 white--text"
                        color="teal"
                        small
                        @click="dialog = true"
                    >
                        {{ $t("common.import") }}
                        <v-icon right dark>mdi-import</v-icon>
                    </v-btn>
                    <v-btn
                        depressed
                        class="white--text ma-2"
                        color="orange"
                        small
                        :loading="loading"
                        @click="verify"
                    >
                        Vérifier
                        <v-icon right>mdi-table-check</v-icon>
                    </v-btn>
                    <v-btn
                        v-if="securityStore.authorities['UPDATE_IMPORT_PARAM']"
                        depressed
                        class="white--text"
                        color="red"
                        small
                        :loading="loading"
                        @click="apply"
                    >
                        {{ $t('common.apply') }}
                        <v-icon right>mdi-database-plus</v-icon>
                    </v-btn>

                </v-toolbar>
                <v-row dense>
                    <v-col cols="4">
                        <v-data-table
                            :headers="headers"
                            :items="compareStates"
                            disable-pagination
                            hide-default-footer
                            dense
                            class="elevation-10"
                            fixed-header
                            height="650"
                            :item-class="rowColor"
                            @click:row="itemClick"
                        >
                            <template v-slot:[`item.occurrences`]="{ item }">
                                <v-chip
                                    class="elevation-5"
                                    color="blue darken-2"
                                    outlined
                                    small
                                >
                                    <strong>{{ item.occurrences }}</strong>
                                </v-chip>
                            </template>
                        </v-data-table>
                    </v-col>
                    <v-col cols="8">
                        <v-data-table
                            :headers="detailHeaders"
                            :items="paySections"
                            disable-pagination
                            hide-default-footer
                            dense
                            class="elevation-10"
                            fixed-header
                            height="650"
                            :item-class="detailRowColor"
                        >
                            <template v-slot:[`item.occurrences`]="{ item }">
                                <v-chip
                                    class="elevation-5"
                                    color="blue darken-2"
                                    outlined
                                    small
                                >
                                    <strong>{{ item.occurrences }}</strong>
                                </v-chip>
                            </template>
                            <template v-slot:[`item.percent`]="{ item }">
                                {{ (item.occurrences * 100 / selected.occurrences).toFixed(2) }} %
                            </template>
                            <template v-slot:[`item.value`]="{ item }">
                                {{ item.value === 0 ? 'Calculé' : priceFormat(item.value) }}
                            </template>
                            <template v-slot:[`item.position`]="{ item }">
                                {{ $t(`common.${item.position}`) }}
                            </template>
                            <template v-slot:[`item.workers`]="{ item }">
                                <v-menu offset-y left>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            color="blue darken-2"
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon>mdi-account-tie-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense>
                                        <v-list-item
                                            dense
                                            v-for="(worker, index) in item.workers"
                                            :key="`worker-${index}`"
                                        >
                                            <v-list-item-action>
                                                <v-list-item-action-text>
                                                    {{ worker.workerCode }}
                                                </v-list-item-action-text>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ worker.workerName }}</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </template>
                            <template v-slot:[`item.action`]="{ item }">
                                <v-btn
                                    v-if="item.common"
                                    icon
                                    color="red"
                                    @click="item.common = false"
                                >
                                    <v-icon>mdi-arrow-down</v-icon>
                                </v-btn>
                                <v-btn
                                    v-else
                                    icon
                                    color="teal"
                                    @click="item.common = true"
                                >
                                    <v-icon>mdi-arrow-up</v-icon>
                                </v-btn>
                            </template>
                        </v-data-table>

                    </v-col>
                </v-row>
            </v-card>
        </v-container>
        <v-dialog
            v-model="dialog"
            persistent
            max-width="700"
        >
            <v-card>
                <v-toolbar flat height="40" dark color="teal">
                    <v-toolbar-title>{{ $t('common.import') }}</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                    <v-container fluid>
                        <v-row dense>
                            <v-col cols="12">
                                <strong>{{ $t('common.file') }} *</strong>
                                <w-file-input
                                    v-model="file"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="12">
                                <strong>{{ $t('common.percent') }} *</strong>
                                <w-text-field
                                    v-model="percent"
                                    type="number"
                                    :rules="[mxRules.required]"
                                />
                            </v-col>
                            <v-col cols="12">
                                <strong>{{ $t('common.pay_sections') }}</strong>
                                <str-pay-section-autocomplete
                                    v-model="onlyByCodePaySections"
                                    multiple
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>
                <v-divider/>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn
                        depressed
                        outlined
                        color="teal"
                        @click="dialog = false"
                    >
                        {{ $t('common.close') }}
                        <v-icon right>mdi-close</v-icon>
                    </v-btn>
                    <v-btn
                        depressed
                        class="white--text"
                        color="teal"
                        :loading="loading"
                        @click="compareState"
                    >
                        {{ $t('common.import') }}
                        <v-icon right>mdi-import</v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import {computed, ref} from "vue";
import useValidation from "@/use/Validation";
import StrPaySectionAutocomplete from "@/components/StrPaySectionAutocomplete.vue";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import axios from "axios";
import ImportModel from "@/views/Tools/Components/ImportModel.vue";
import {useSecurityStore} from "@/stores/Security";

export default {
    name: "ImportOccupationPaySection",
    components: {ImportModel, StrPaySectionAutocomplete},
    setup() {
        const {mxRules} = useValidation();
        const {priceFormat} = useSharedMethod();
        const {dialog, loading, itemColor} = useSharedData();
        const securityStore = useSecurityStore();
        dialog.value = false;

        const file = ref(null);
        const percent = ref(100);
        const onlyByCodePaySections = ref([]);
        const compareStates = ref([]);
        const selected = ref({paySections: []});

        const headers = [
            {text: 'Name', value: 'occupationName'},
            {text: 'Occurrences', value: 'occurrences'},
        ];

        const detailHeaders = [
            {text: 'Code', value: 'paySectionCode'},
            {text: 'Libellé', value: 'paySectionLabel'},
            {text: 'Occurrences', value: 'occurrences'},
            {text: 'Pourcentage', value: 'percent'},
            {text: 'Valeur', value: 'value'},
            {text: 'Position', value: 'position'},
            {text: 'Employés', value: 'workers'},
            {text: '', value: 'action'},
        ]

        function compareState() {
            loading.value = true;
            let formData = new FormData();
            formData.append('file', file.value);
            axios.post(`/tools/getAssignedPaySectionCompareState`, formData, {
                params: {
                    percent: percent.value,
                    onlyByCodePaySections: onlyByCodePaySections.value.toString()
                }
            }).then(response => {
                compareStates.value = response.data;
                if (compareStates.value.length > 0)
                    selected.value = compareStates.value[0];
                dialog.value = false;
                file.value = null;
            }).finally(() => {
                loading.value = false;
            });
        }

        function verify() {
            loading.value = true;
            axios.get(`/tools/verifyAssignedPaySectionCompareState`).then(response => {
                compareStates.value = response.data;
                if (compareStates.value.length > 0)
                    selected.value = compareStates.value[0];
            }).finally(() => {
                loading.value = false;
            });
        }

        function apply() {
            loading.value = true;
            let formData = new FormData();
            formData.append('file', file.value);
            axios.post(`/tools/applyAssignedPaySectionCompareState`, compareStates.value).then(() => {
                // compareStates.value = response.data;
                // dialog.value = false;
                // file.value = null;
            }).finally(() => {
                loading.value = false;
            });
        }

        const paySections = computed(() => selected.value.paySections.sort((a, b) => {
            if (a.common === b.common) {
                if (a.paySectionOrder === b.paySectionOrder)
                    return b.occurrences - a.occurrences;
                return a.paySectionOrder - b.paySectionOrder;
            }
            return a.common ? -1 : 1;
        }));

        function itemClick(item) {
            selected.value = item;
        }

        function rowColor(item) {
            if (item.occupationId === selected.value.occupationId) {
                return itemColor.value;
            }
        }

        function detailRowColor(item) {
            if (item.common) {
                return 'teal lighten-4';
            } else {
                return "red lighten-4";
            }
        }

        return {
            securityStore,
            mxRules,
            dialog,
            loading,
            file,
            percent,
            onlyByCodePaySections,
            compareStates,
            selected,
            paySections,
            headers,
            detailHeaders,
            rowColor,
            detailRowColor,
            itemClick,
            priceFormat,
            compareState,
            verify,
            apply
        }
    }
}
</script>

<style scoped>

</style>